import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import NavBar from "../components/NavBar";
import { toast } from "sonner";
import { Boxes, MessageSquare, Server, LogOut, Plus, Pencil, Trash2, Wallet, Package, Star, Check, ShieldCheck, Truck, Activity, Radio, Tag, X } from "lucide-react";

const tabClass = ({ isActive }) =>
  `font-mono-tech text-xs uppercase tracking-widest px-4 py-2 border-2 ${
    isActive ? "border-zinc-300 text-zinc-200 bg-zinc-900" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
  }`;

const useAuthCheck = () => {
  const [ok, setOk] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem("bleak_admin_token");
    if (!token) { setOk(false); navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => setOk(true)).catch(() => { localStorage.removeItem("bleak_admin_token"); setOk(false); navigate("/admin/login"); });
  }, [navigate]);
  return ok;
};

// ----- Products tab -----
const blankProduct = { name: "", category: "hoodie", price: 0, sale_price: null, sale_ends_at: null, description: "", sizes: ["S", "M", "L", "XL"], stock: 50, image_url: "", featured: false, limited_edition: false, coming_soon: false, sold_out: false, active: true };

const ProductsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // product or "new"
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) {
        await api.patch(`/admin/products/${data.id}`, data);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", data);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="font-display text-2xl sm:text-3xl text-zinc-50 uppercase leading-none">Products</h2>
        <button type="button" onClick={() => setEditing({ ...blankProduct })} data-testid="add-product-btn" className="inline-flex items-center gap-2 bg-zinc-300 text-zinc-950 px-3 sm:px-4 py-2 font-display text-xs sm:text-sm tracking-widest uppercase whitespace-nowrap">
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-zinc-500 text-sm uppercase">// Loading...</div>
      ) : (
        <div data-testid="admin-products-table">
          {/* Mobile card list */}
          <div className="sm:hidden space-y-3">
            {items.map((p) => (
              <div key={p.id} className="border-2 border-zinc-800 p-3" data-testid={`card-${p.slug}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base text-zinc-100 uppercase truncate">{p.name}</div>
                    <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-0.5">{p.category} · stock {p.stock} · {p.active ? <span className="text-lime-500">on</span> : <span className="text-zinc-500">off</span>}</div>
                    <div className="mt-1 font-mono-tech text-sm">
                      {p.sale_price != null && Number(p.sale_price) < Number(p.price) ? (
                        <><span className="text-zinc-500 line-through mr-2">${p.price.toFixed(2)}</span><span className="text-red-500">${Number(p.sale_price).toFixed(2)}</span></>
                      ) : (
                        <span className="text-zinc-200">${p.price.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button type="button" onClick={() => setEditing({ ...p, sizes: p.sizes || [] })} className="p-2 border-2 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-300" data-testid={`edit-mobile-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                    <button type="button" onClick={() => del(p.id)} className="p-2 border-2 border-zinc-800 text-zinc-400 hover:text-red-500 hover:border-red-600" data-testid={`del-mobile-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="p-6 text-center font-mono-tech text-zinc-500 uppercase text-xs border-2 border-dashed border-zinc-800">// No products yet</div>}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block border-2 border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 font-mono-tech text-xs uppercase text-zinc-400">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Price</th>
                <th className="text-left p-3">Stock</th>
                <th className="text-left p-3">Active</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/60 transition-colors" data-testid={`row-${p.slug}`}>
                  <td className="p-3 text-zinc-100">{p.name}</td>
                  <td className="p-3 font-mono-tech uppercase text-zinc-400">{p.category}</td>
                  <td className="p-3 font-mono-tech">
                    {p.sale_price != null && Number(p.sale_price) < Number(p.price) ? (
                      <span>
                        <span className="text-zinc-500 line-through mr-2">${p.price.toFixed(2)}</span>
                        <span className="text-red-500">${Number(p.sale_price).toFixed(2)}</span>
                      </span>
                    ) : (
                      <span className="text-zinc-200">${p.price.toFixed(2)}</span>
                    )}
                  </td>
                  <td className="p-3 font-mono-tech text-zinc-300">{p.stock}</td>
                  <td className="p-3 font-mono-tech text-xs uppercase">{p.active ? <span className="text-lime-500">on</span> : <span className="text-zinc-500">off</span>}</td>
                  <td className="p-3 text-right">
                    <button type="button" onClick={() => setEditing({ ...p, sizes: p.sizes || [] })} className="text-zinc-400 hover:text-zinc-200 mr-2" data-testid={`edit-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                    <button type="button" onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-500" data-testid={`del-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center font-mono-tech text-zinc-500 uppercase text-xs">// No products yet</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {editing && (
        <ProductEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
};

const ProductEditor = ({ initial, onCancel, onSave }) => {
  const [form, setForm] = useState(initial);
  const [sizesStr, setSizesStr] = useState((initial.sizes || []).join(","));
  // Store the datetime-local input value (yyyy-MM-ddTHH:mm, no timezone)
  const [saleEndsLocal, setSaleEndsLocal] = useState(() => {
    if (!initial.sale_ends_at) return "";
    const d = new Date(initial.sale_ends_at);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const set = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const quickSaleDays = (days) => {
    const end = new Date(Date.now() + days * 86400000);
    const pad = (n) => String(n).padStart(2, "0");
    setSaleEndsLocal(`${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`);
  };
  const clearSale = () => {
    setForm((f) => ({ ...f, sale_price: null }));
    setSaleEndsLocal("");
  };

  const submit = (e) => {
    e.preventDefault();
    const sizes = sizesStr.split(",").map((s) => s.trim()).filter(Boolean);
    const salePriceNum = form.sale_price === "" || form.sale_price == null
      ? null
      : Number(form.sale_price);
    const saleEndsIso = saleEndsLocal ? new Date(saleEndsLocal).toISOString() : null;
    onSave({
      ...form,
      sizes,
      price: Number(form.price),
      stock: Number(form.stock),
      sale_price: salePriceNum,
      sale_ends_at: saleEndsIso,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-auto" data-testid="product-editor">
      <form onSubmit={submit} className="bg-zinc-950 border-2 border-zinc-800 brutal-shadow p-4 sm:p-6 max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-auto my-2 sm:my-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl sm:text-2xl text-zinc-50 uppercase">{form.id ? "Edit" : "New"} Product</h3>
          <button type="button" onClick={onCancel} className="font-mono-tech text-xs uppercase text-zinc-400 border border-zinc-700 px-3 py-1 hover:text-red-500 hover:border-red-600" data-testid="editor-close">Close</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name *"><input required value={form.name} onChange={set("name")} className={fieldCls} data-testid="editor-name" /></Field>
          <Field label="Category">
            <select value={form.category} onChange={set("category")} className={fieldCls}>
              {["hoodie", "tee", "headwear", "pants", "accessory", "outerwear"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Price USD *"><input type="number" step="0.01" required value={form.price} onChange={set("price")} className={fieldCls} data-testid="editor-price" /></Field>
          <Field label="Stock"><input type="number" value={form.stock} onChange={set("stock")} className={fieldCls} /></Field>
          <Field label="Image URL *"><input required value={form.image_url} onChange={set("image_url")} className={fieldCls} data-testid="editor-image" /></Field>
          <Field label="Sizes (csv)"><input value={sizesStr} onChange={(e) => setSizesStr(e.target.value)} className={fieldCls} placeholder="S,M,L,XL" /></Field>
          <div className="sm:col-span-2">
            <Field label="Description"><textarea rows={3} value={form.description} onChange={set("description")} className={fieldCls} /></Field>
          </div>

          {/* SALE controls */}
          <div className="sm:col-span-2 border-2 border-red-900/60 bg-red-950/20 p-3 space-y-3" data-testid="editor-sale-section">
            <div className="flex items-center justify-between">
              <div className="font-mono-tech text-xs uppercase text-red-400 tracking-widest">Limited-Time Sale</div>
              {(form.sale_price || saleEndsLocal) && (
                <button type="button" onClick={clearSale} className="font-mono-tech text-[10px] uppercase text-zinc-400 hover:text-red-400" data-testid="editor-sale-clear">Remove Sale</button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={`Sale Price USD (blank = no sale)`}>
                <input
                  type="number"
                  step="0.01"
                  value={form.sale_price ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, sale_price: e.target.value === "" ? null : Number(e.target.value) }))}
                  className={fieldCls}
                  placeholder={`< ${Number(form.price || 0).toFixed(2)}`}
                  data-testid="editor-sale-price"
                />
              </Field>
              <Field label="Sale Ends (blank = no end)">
                <input
                  type="datetime-local"
                  value={saleEndsLocal}
                  onChange={(e) => setSaleEndsLocal(e.target.value)}
                  className={fieldCls}
                  data-testid="editor-sale-ends"
                />
              </Field>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="font-mono-tech text-[10px] uppercase text-zinc-500 self-center">Quick set:</span>
              {[
                { label: "1 hr", d: 1 / 24 },
                { label: "1 day", d: 1 },
                { label: "3 days", d: 3 },
                { label: "7 days", d: 7 },
                { label: "14 days", d: 14 },
              ].map((q) => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => quickSaleDays(q.d)}
                  className="font-mono-tech text-[10px] uppercase px-2 py-1 border border-red-800 text-red-300 hover:bg-red-900/40"
                  data-testid={`editor-sale-quick-${q.label.replace(/\s+/g, "")}`}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 font-mono-tech text-xs uppercase text-zinc-300">
            <input type="checkbox" checked={!!form.featured} onChange={set("featured")} /> Featured
          </label>
          <label className="flex items-center gap-2 font-mono-tech text-xs uppercase text-pink-400">
            <input type="checkbox" checked={!!form.limited_edition} onChange={set("limited_edition")} /> Limited Edition
          </label>
          <label className="flex items-center gap-2 font-mono-tech text-xs uppercase text-zinc-100">
            <input type="checkbox" checked={!!form.coming_soon} onChange={set("coming_soon")} data-testid="editor-coming-soon" /> Coming Soon
          </label>
          <label className="flex items-center gap-2 font-mono-tech text-xs uppercase text-red-400">
            <input type="checkbox" checked={!!form.sold_out} onChange={set("sold_out")} data-testid="editor-sold-out" /> Sold Out
          </label>
          <label className="flex items-center gap-2 font-mono-tech text-xs uppercase text-zinc-300">
            <input type="checkbox" checked={form.active !== false} onChange={set("active")} /> Active
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="border-2 border-zinc-700 px-4 py-2 font-mono-tech text-xs uppercase text-zinc-300">Cancel</button>
          <button type="submit" className="bg-zinc-300 text-zinc-950 px-4 py-2 font-display text-sm tracking-widest uppercase" data-testid="editor-save">Save</button>
        </div>
      </form>
    </div>
  );
};

const fieldCls = "w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-xs uppercase text-zinc-400 block mb-1">{label}</span>
    {children}
  </label>
);

// ----- Server info tab -----
const ServerInfoTab = () => {
  const [info, setInfo] = useState(null);
  const [rulesStr, setRulesStr] = useState("");
  const [modsStr, setModsStr] = useState("");
  const [featStr, setFeatStr] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/server-info").then((r) => {
      setInfo(r.data);
      setRulesStr(r.data.rules.join("\n"));
      setModsStr(r.data.mods.join(","));
      setFeatStr(r.data.features.join(","));
    }).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...info,
        rules: rulesStr.split("\n").map((r) => r.trim()).filter(Boolean),
        mods: modsStr.split(",").map((m) => m.trim()).filter(Boolean),
        features: featStr.split(",").map((m) => m.trim()).filter(Boolean),
        max_players: Number(info.max_players),
        current_players: Number(info.current_players || 0),
      };
      const { data } = await api.put("/admin/server-info", payload);
      setInfo(data);
      toast.success("Server info updated");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !info) return <div className="font-mono-tech text-zinc-500 uppercase text-sm">// Loading...</div>;
  const set = (k) => (e) => setInfo({ ...info, [k]: e.target.value });

  return (
    <form onSubmit={save} className="space-y-4" data-testid="admin-server-info">
      <h2 className="font-display text-3xl text-zinc-50 uppercase leading-none">DayZ Server Info</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Server Name"><input value={info.server_name} onChange={set("server_name")} className={fieldCls} data-testid="server-name" /></Field>
        <Field label="Tagline"><input value={info.tagline} onChange={set("tagline")} className={fieldCls} /></Field>
        <Field label="IP / Host"><input value={info.ip} onChange={set("ip")} className={fieldCls} data-testid="server-ip" /></Field>
        <Field label="Port"><input value={info.port} onChange={set("port")} className={fieldCls} data-testid="server-port" /></Field>
        <Field label="Query Port (blank = port+1)"><input value={info.query_port || ""} onChange={set("query_port")} className={fieldCls} data-testid="srv-query-port" placeholder="auto" /></Field>
        <Field label="Discord URL"><input value={info.discord_url} onChange={set("discord_url")} className={fieldCls} /></Field>
        <Field label="Map"><input value={info.map} onChange={set("map")} className={fieldCls} /></Field>
        <Field label="Max Players"><input type="number" value={info.max_players} onChange={set("max_players")} className={fieldCls} /></Field>
        <Field label="Current Players"><input type="number" value={info.current_players ?? 0} onChange={set("current_players")} className={fieldCls} /></Field>
        <div className="sm:col-span-2"><Field label="Description"><textarea rows={3} value={info.description} onChange={set("description")} className={fieldCls} /></Field></div>
        <div className="sm:col-span-2"><Field label="Rules (one per line)"><textarea rows={5} value={rulesStr} onChange={(e) => setRulesStr(e.target.value)} className={fieldCls} /></Field></div>
        <Field label="Mods (csv)"><input value={modsStr} onChange={(e) => setModsStr(e.target.value)} className={fieldCls} /></Field>
        <Field label="Features (csv)"><input value={featStr} onChange={(e) => setFeatStr(e.target.value)} className={fieldCls} /></Field>
      </div>
      <button type="submit" disabled={saving} data-testid="save-server-info" className="bg-zinc-300 disabled:opacity-60 text-zinc-950 px-6 py-3 font-display text-base tracking-widest uppercase">{saving ? "Saving..." : "Save Server Info"}</button>
    </form>
  );
};

// ----- Inquiries tab -----
const InquiriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/inquiries").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="admin-inquiries">
      <h2 className="font-display text-3xl text-zinc-50 uppercase mb-4 leading-none">Inquiries</h2>
      {loading ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// Loading...</div>
      ) : items.length === 0 ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// No inquiries yet</div>
      ) : (
        <div className="space-y-3">
          {items.map((q) => (
            <div key={q.id} className="border-2 border-zinc-800 p-4 bg-zinc-950" data-testid={`inquiry-${q.id}`}>
              <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                  <div className="font-display text-lg text-zinc-50 uppercase">{q.subject}</div>
                  <div className="font-mono-tech text-xs uppercase text-zinc-500">{q.name} // {q.email} // {q.inquiry_type}</div>
                </div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500">{q.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
              <p className="mt-3 text-zinc-300 whitespace-pre-wrap text-sm">{q.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ----- Payments tab -----
const PaymentsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/payments").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="admin-payments">
      <h2 className="font-display text-3xl text-zinc-50 uppercase mb-4 leading-none">Payments</h2>
      {loading ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// Loading...</div>
      ) : items.length === 0 ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// No payments yet</div>
      ) : (
        <div className="border-2 border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 font-mono-tech text-xs uppercase text-zinc-400">
              <tr><th className="text-left p-3">Session</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Email</th><th className="text-left p-3">When</th></tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-zinc-800">
                  <td className="p-3 font-mono-tech text-xs text-zinc-300">{t.session_id?.slice(0, 16)}…</td>
                  <td className="p-3 font-mono-tech text-zinc-200">${Number(t.amount).toFixed(2)}</td>
                  <td className="p-3 font-mono-tech text-xs uppercase">
                    <span className={t.payment_status === "paid" ? "text-lime-500" : "text-zinc-400"}>{t.payment_status}</span>
                  </td>
                  <td className="p-3 font-mono-tech text-xs text-zinc-400">{t.customer_email || "—"}</td>
                  <td className="p-3 font-mono-tech text-xs text-zinc-500">{t.created_at?.slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ----- Orders tab -----
// ----- Tracking editor (inline on each order) -----
const CARRIER_OPTIONS = [
  { value: "", label: "-- Select --" },
  { value: "usps", label: "USPS" },
  { value: "ups", label: "UPS" },
  { value: "fedex", label: "FedEx" },
  { value: "dhl", label: "DHL" },
  { value: "other", label: "Other" },
];

const TrackingEditor = ({ order, onSaved }) => {
  const [carrier, setCarrier] = useState(order.tracking_carrier || "");
  const [number, setNumber] = useState(order.tracking_number || "");
  const [url, setUrl] = useState(order.tracking_url || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        tracking_carrier: carrier || null,
        tracking_number: number.trim() || null,
        tracking_url: url.trim() || null,
      };
      // Auto-mark shipped when tracking number is added and it's not shipped yet
      if (payload.tracking_number && order.fulfillment_status !== "shipped") {
        payload.fulfillment_status = "shipped";
      }
      await api.patch(`/admin/orders/${order.id}`, payload);
      toast.success("Tracking saved");
      onSaved && onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid={`tracking-editor-${order.order_number}`}>
      <div>
        <label className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest block mb-1">Carrier</label>
        <select
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          data-testid={`tracking-carrier-${order.order_number}`}
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-xs text-zinc-100 uppercase"
        >
          {CARRIER_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest block mb-1">Tracking Number</label>
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="9400111899223..."
          data-testid={`tracking-number-${order.order_number}`}
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-xs text-zinc-100"
        />
      </div>
      <div>
        <label className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest block mb-1">
          Tracking URL <span className="text-zinc-600 normal-case">(auto if blank)</span>
        </label>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Leave blank to auto-build"
          data-testid={`tracking-url-${order.order_number}`}
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-xs text-zinc-100"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          data-testid={`tracking-save-${order.order_number}`}
          className="font-mono-tech text-xs uppercase bg-zinc-100 text-zinc-950 px-4 py-2 hover:bg-white disabled:opacity-60 inline-flex items-center gap-1"
        >
          <Truck className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
        </button>
        {order.tracking_url && (
          <a
            href={order.tracking_url}
            target="_blank"
            rel="noreferrer"
            className="font-mono-tech text-xs uppercase border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-zinc-300 hover:text-zinc-100"
          >
            View
          </a>
        )}
      </div>
    </form>
  );
};

// ----- Orders tab -----
const OrdersTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, patch) => {
    try {
      await api.patch(`/admin/orders/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-orders">
      <h2 className="font-display text-3xl text-zinc-50 uppercase mb-4 leading-none">Orders</h2>
      {loading ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// Loading...</div>
      ) : items.length === 0 ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// No orders yet</div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.id} className="border-2 border-zinc-800 bg-zinc-950" data-testid={`order-${o.order_number}`}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                className="w-full p-4 flex flex-wrap justify-between items-center gap-3 text-left hover:bg-zinc-900"
              >
                <div className="flex flex-col">
                  <div className="font-display text-lg text-zinc-50 uppercase">{o.order_number}</div>
                  <div className="font-mono-tech text-xs uppercase text-zinc-500">{o.customer_name} // {o.customer_email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono-tech text-xs uppercase px-2 py-1 border ${
                    o.payment_status === "paid" ? "border-lime-700 text-lime-500"
                    : o.payment_status === "cancelled" ? "border-red-700 text-red-500"
                    : "border-zinc-400 text-zinc-200"
                  }`}>{o.payment_status}</span>
                  <span className={`font-mono-tech text-xs uppercase px-2 py-1 border ${
                    o.fulfillment_status === "shipped" ? "border-lime-700 text-lime-500" : "border-zinc-700 text-zinc-400"
                  }`}>{o.fulfillment_status}</span>
                  {o.tracking_number && (
                    <span className="font-mono-tech text-[10px] uppercase text-zinc-400 tracking-widest inline-flex items-center gap-1" title={o.tracking_number}>
                      <Truck className="w-3 h-3" /> {(o.tracking_carrier || "").toUpperCase() || "TRK"}
                    </span>
                  )}
                  <div className="font-mono-tech text-zinc-200">${o.total.toFixed(2)}</div>
                </div>
              </button>
              {expanded === o.id && (
                <div className="border-t-2 border-zinc-800 p-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-mono-tech text-xs uppercase text-zinc-500 mb-1">Customer</div>
                    <div className="text-zinc-100">{o.customer_name}</div>
                    <div className="text-zinc-300 text-xs">{o.customer_email}</div>
                    {o.customer_discord && <div className="text-zinc-400 text-xs">Discord: {o.customer_discord}</div>}
                    <div className="font-mono-tech text-xs uppercase text-zinc-500 mt-3 mb-1">Placed</div>
                    <div className="text-zinc-300 text-xs">{o.created_at?.slice(0, 19).replace("T", " ")}</div>
                  </div>
                  <div>
                    <div className="font-mono-tech text-xs uppercase text-zinc-500 mb-1">Ship To</div>
                    <div className="text-zinc-200 text-xs whitespace-pre-line">
                      {o.shipping_line1}{o.shipping_line2 ? "\n" + o.shipping_line2 : ""}
                      {"\n"}{o.shipping_city}{o.shipping_state ? ", " + o.shipping_state : ""} {o.shipping_postal}
                      {"\n"}{o.shipping_country}
                    </div>
                    {o.notes && <><div className="font-mono-tech text-xs uppercase text-zinc-500 mt-3 mb-1">Notes</div><div className="text-zinc-300 text-xs">{o.notes}</div></>}
                  </div>
                  <div>
                    <div className="font-mono-tech text-xs uppercase text-zinc-500 mb-1">Items</div>
                    <ul className="space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="text-zinc-200 text-xs">
                          {it.quantity}× {it.name} <span className="text-zinc-500">[{it.size}{it.color ? ` / ${it.color}` : ""}]</span> — ${(it.price * it.quantity).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 font-mono-tech text-zinc-200 text-base">Total ${o.total.toFixed(2)}</div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {o.payment_status !== "paid" && (
                        <button type="button" onClick={() => updateStatus(o.id, { payment_status: "paid" })} className="font-mono-tech text-xs uppercase bg-lime-600 text-zinc-950 px-3 py-1.5" data-testid={`mark-paid-${o.order_number}`}>Mark Paid</button>
                      )}
                      {o.payment_status !== "cancelled" && (
                        <button type="button" onClick={() => updateStatus(o.id, { payment_status: "cancelled" })} className="font-mono-tech text-xs uppercase border-2 border-red-700 text-red-500 px-3 py-1.5">Cancel</button>
                      )}
                      {o.fulfillment_status !== "shipped" && (
                        <button type="button" onClick={() => updateStatus(o.id, { fulfillment_status: "shipped" })} className="font-mono-tech text-xs uppercase border-2 border-zinc-700 text-zinc-200 px-3 py-1.5" data-testid={`mark-shipped-${o.order_number}`}>Mark Shipped</button>
                      )}
                    </div>
                  </div>

                  {/* Tracking editor (spans full width) */}
                  <div className="md:col-span-3 border-t border-zinc-800 pt-4 mt-1">
                    <TrackingEditor order={o} onSaved={load} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ----- Reviews tab -----
const ReviewsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = () => {
    setLoading(true);
    api.get("/admin/reviews").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, body, successMsg) => {
    try {
      await api.patch(`/admin/reviews/${id}`, body);
      toast.success(successMsg);
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((r) => {
    if (filter === "pending") return !r.approved;
    if (filter === "approved") return r.approved;
    return true;
  });
  const counts = {
    pending: items.filter((r) => !r.approved).length,
    approved: items.filter((r) => r.approved).length,
    all: items.length,
  };

  return (
    <div data-testid="admin-reviews">
      <h2 className="font-display text-3xl text-zinc-50 uppercase mb-4 leading-none">Reviews</h2>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-reviews-tabs">
        {[
          { id: "pending", label: `PENDING (${counts.pending})` },
          { id: "approved", label: `APPROVED (${counts.approved})` },
          { id: "all", label: `ALL (${counts.all})` },
        ].map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`admin-reviews-filter-${f.id}`}
              className={`font-mono-tech text-xs uppercase tracking-widest px-3 py-2 border-2 transition-colors ${
                active ? "bg-zinc-100 border-zinc-100 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// No reviews in this bucket</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-review-${r.id}`}
              className={`border-2 ${r.approved ? "border-zinc-800" : "border-zinc-300/60"} bg-zinc-950 p-4`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-lg text-zinc-50 uppercase tracking-widest">{r.name}</span>
                  {r.location && <span className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest">// {r.location}</span>}
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-zinc-200 text-zinc-200" : "text-zinc-700"}`} />
                    ))}
                  </div>
                  {r.approved ? (
                    <span className="border border-emerald-600 text-emerald-500 font-mono-tech text-[10px] uppercase tracking-widest px-2 py-0.5">APPROVED</span>
                  ) : (
                    <span className="border border-zinc-200 text-zinc-100 font-mono-tech text-[10px] uppercase tracking-widest px-2 py-0.5">PENDING</span>
                  )}
                  {r.verified_purchase && (
                    <span className="inline-flex items-center gap-1 border border-emerald-600 text-emerald-500 font-mono-tech text-[10px] uppercase tracking-widest px-2 py-0.5">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest">
                  {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <p className="text-zinc-200 leading-relaxed mb-3">&ldquo;{r.quote}&rdquo;</p>
              {r.email && (
                <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mb-3">// {r.email}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {!r.approved ? (
                  <button
                    onClick={() => patch(r.id, { approved: true }, "Approved")}
                    data-testid={`review-approve-${r.id}`}
                    className="bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-mono-tech text-xs uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1"
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                ) : (
                  <button
                    onClick={() => patch(r.id, { approved: false }, "Un-approved")}
                    data-testid={`review-unapprove-${r.id}`}
                    className="border-2 border-zinc-700 text-zinc-300 font-mono-tech text-xs uppercase tracking-widest px-3 py-1.5 hover:border-zinc-500"
                  >
                    Un-approve
                  </button>
                )}
                <button
                  onClick={() => patch(r.id, { verified_purchase: !r.verified_purchase }, r.verified_purchase ? "Verified badge removed" : "Marked verified")}
                  data-testid={`review-verify-${r.id}`}
                  className="border-2 border-zinc-700 text-zinc-300 font-mono-tech text-xs uppercase tracking-widest px-3 py-1.5 hover:border-emerald-500 hover:text-emerald-400 inline-flex items-center gap-1"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> {r.verified_purchase ? "Remove Verified" : "Mark Verified"}
                </button>
                <button
                  onClick={() => remove(r.id)}
                  data-testid={`review-delete-${r.id}`}
                  className="border-2 border-red-700 text-red-500 font-mono-tech text-xs uppercase tracking-widest px-3 py-1.5 hover:bg-red-700 hover:text-zinc-50 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ----- Layout -----
const AdminStatsStrip = () => {
  const [stats, setStats] = useState({ products: 0, orders: 0, pending: 0, revenue: 0 });
  useEffect(() => {
    Promise.all([
      api.get("/admin/products").catch(() => ({ data: [] })),
      api.get("/admin/orders").catch(() => ({ data: [] })),
    ]).then(([p, o]) => {
      const orders = o.data || [];
      const revenue = orders
        .filter((x) => x.payment_status === "paid")
        .reduce((a, x) => a + Number(x.total || 0), 0);
      const pending = orders.filter(
        (x) => x.payment_status === "pending" || x.fulfillment_status === "unfulfilled",
      ).length;
      setStats({ products: (p.data || []).length, orders: orders.length, pending, revenue });
    });
  }, []);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-stats-strip">
      <StatCell label="Products" value={stats.products} testid="stat-products" />
      <StatCell label="Orders" value={stats.orders} testid="stat-orders" />
      <StatCell label="Pending" value={stats.pending} testid="stat-pending" />
      <StatCell label="Revenue" value={`$${stats.revenue.toFixed(0)}`} testid="stat-revenue" />
    </div>
  );
};

const StatCell = ({ label, value, testid }) => (
  <div className="border-2 border-zinc-800 bg-zinc-900/60 p-3 sm:p-4" data-testid={testid}>
    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="font-display text-xl sm:text-2xl text-zinc-50 mt-1">{value}</div>
  </div>
);

const AdminPage = () => {
  const auth = useAuthCheck();
  const navigate = useNavigate();
  const logout = () => { localStorage.removeItem("bleak_admin_token"); navigate("/admin/login"); };

  if (auth === null) return <div className="min-h-screen bg-zinc-950"><NavBar /><div className="py-32 text-center font-mono-tech text-zinc-500 uppercase">// Checking auth ...</div></div>;
  if (auth === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-zinc-950">
      <NavBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b-2 border-zinc-800 pb-5 mb-6">
          <div>
            <div className="font-mono-tech text-[10px] tracking-widest text-zinc-200 uppercase">[ Control Room / PandemicStyles ]</div>
            <h1 className="font-display text-4xl sm:text-5xl text-zinc-50 uppercase leading-none mt-1">Admin</h1>
          </div>
          <button type="button" onClick={logout} className="self-start sm:self-auto font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-red-500 flex items-center gap-2" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>

        <AdminStatsStrip />

        <div className="-mx-4 sm:mx-0 px-4 sm:px-0 flex flex-nowrap sm:flex-wrap gap-2 mb-8 border-b border-zinc-800 pb-4 overflow-x-auto sm:overflow-visible" data-testid="admin-tabs">
          <NavLink to="/admin" end className={tabClass} data-testid="tab-products"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Boxes className="w-3 h-3" /> Products</span></NavLink>
          <NavLink to="/admin/orders" className={tabClass} data-testid="tab-orders"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Package className="w-3 h-3" /> Orders</span></NavLink>
          <NavLink to="/admin/inquiries" className={tabClass} data-testid="tab-inquiries"><span className="inline-flex items-center gap-2 whitespace-nowrap"><MessageSquare className="w-3 h-3" /> Inquiries</span></NavLink>
          <NavLink to="/admin/reviews" className={tabClass} data-testid="tab-reviews"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Star className="w-3 h-3" /> Reviews</span></NavLink>
          <NavLink to="/admin/traffic" className={tabClass} data-testid="tab-traffic"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Activity className="w-3 h-3" /> Traffic</span></NavLink>
          <NavLink to="/admin/payments" className={tabClass} data-testid="tab-payments"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Wallet className="w-3 h-3" /> Payments</span></NavLink>
          <NavLink to="/admin/coupons" className={tabClass} data-testid="tab-coupons"><span className="inline-flex items-center gap-2 whitespace-nowrap"><Tag className="w-3 h-3" /> Coupons</span></NavLink>
        </div>

        <Routes>
          <Route index element={<ProductsTab />} />
          <Route path="orders" element={<OrdersTab />} />
          <Route path="inquiries" element={<InquiriesTab />} />
          <Route path="reviews" element={<ReviewsTab />} />
          <Route path="traffic" element={<TrafficTab />} />
          <Route path="payments" element={<PaymentsTab />} />
          <Route path="coupons" element={<CouponsTab />} />
        </Routes>
      </div>
    </div>
  );
};

// ----- Traffic tab (live visitor analytics) -----
const TrafficTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/analytics/live").then((r) => setStats(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    const id = setInterval(load, 10_000); // poll every 10s
    return () => clearInterval(id);
  }, []);

  const secondsAgo = (iso) => {
    if (!iso) return "—";
    const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div data-testid="admin-traffic">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-3xl text-zinc-50 uppercase leading-none">Live Traffic</h2>
          <div className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mt-2">
            // Refreshes every 10 seconds. Admin browsing is excluded.
          </div>
        </div>
        <div className="flex items-center gap-2 font-mono-tech text-xs uppercase text-emerald-400">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </div>
      </div>

      {loading && !stats ? (
        <div className="font-mono-tech text-zinc-500 uppercase text-sm">// Loading...</div>
      ) : !stats ? (
        <div className="font-mono-tech text-red-500 uppercase text-sm">// Failed to load traffic data</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { label: "Active now", value: stats.active_now, icon: Radio, hint: "last 5 min" },
              { label: "Views today", value: stats.page_views_today, icon: Activity, hint: `${stats.page_views_yesterday} yesterday` },
              { label: "Unique today", value: stats.unique_visitors_today, icon: ShieldCheck, hint: "distinct visitors" },
              { label: "Top page", value: (stats.top_pages_today?.[0]?.path?.slice(0, 18)) || "—", icon: Package, hint: `${stats.top_pages_today?.[0]?.views || 0} views`, isText: true },
            ].map((k, i) => (
              <div key={i} className="border border-zinc-800 bg-zinc-900/40 p-4">
                <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest mb-2">
                  <k.icon className="w-3 h-3" /> {k.label}
                </div>
                <div className={`font-display ${k.isText ? "text-base" : "text-3xl"} text-zinc-50 leading-none`}>
                  {k.value}
                </div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-2">// {k.hint}</div>
              </div>
            ))}
          </div>

          {/* Top pages */}
          {stats.top_pages_today && stats.top_pages_today.length > 0 && (
            <div className="mb-8">
              <h3 className="font-display text-xl text-zinc-100 uppercase mb-3 tracking-widest">Top pages today</h3>
              <div className="border border-zinc-800 divide-y divide-zinc-800">
                {stats.top_pages_today.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5">
                    <span className="font-mono-tech text-xs text-zinc-200 truncate">{p.path}</span>
                    <span className="font-mono-tech text-xs text-zinc-400 uppercase tracking-widest">{p.views} views</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active visitors */}
          <div>
            <h3 className="font-display text-xl text-zinc-100 uppercase mb-3 tracking-widest">
              Active visitors <span className="text-zinc-500">({stats.visitors.length})</span>
            </h3>
            {stats.visitors.length === 0 ? (
              <div className="border border-zinc-800 bg-zinc-900/40 p-6 text-center font-mono-tech text-xs uppercase text-zinc-500">
                // No one is on the site right now
              </div>
            ) : (
              <div className="border border-zinc-800 overflow-x-auto">
                <table className="w-full text-left" data-testid="traffic-visitor-table">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-900/40 font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest">
                      <th className="px-4 py-2">Visitor</th>
                      <th className="px-4 py-2">Currently on</th>
                      <th className="px-4 py-2">Location</th>
                      <th className="px-4 py-2 text-right">Last ping</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.visitors.map((v) => (
                      <tr key={v.visitor_id} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/40">
                        <td className="px-4 py-2 font-mono-tech text-xs text-zinc-400">{v.visitor_id.slice(0, 12)}…</td>
                        <td className="px-4 py-2 font-mono-tech text-xs text-zinc-100 truncate max-w-xs">{v.path}</td>
                        <td className="px-4 py-2 font-mono-tech text-xs text-zinc-300 uppercase">{v.country || "—"}</td>
                        <td className="px-4 py-2 font-mono-tech text-xs text-zinc-400 uppercase text-right">{secondsAgo(v.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPage;

// ----- Coupons tab -----
const CouponsTab = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    discount_value: 10,
    expires_at: "",
    max_uses: "",
    min_order: "",
    once_per_customer: false,
  });

  const load = () => {
    setLoading(true);
    api.get("/admin/coupons")
      .then((r) => setCoupons(r.data))
      .catch(() => toast.error("Could not load coupons"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Code required"); return; }
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        min_order: form.min_order ? parseFloat(form.min_order) : null,
        once_per_customer: !!form.once_per_customer,
      };
      await api.post("/admin/coupons", payload);
      toast.success(`Created ${payload.code}`);
      setForm({ code: "", discount_type: "percent", discount_value: 10, expires_at: "", max_uses: "", min_order: "", once_per_customer: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create coupon");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.code}`, { active: !c.active });
      load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${code}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  // Giveaway generator — creates a unique single-use $ off code, copies to clipboard.
  const [giveawayBusy, setGiveawayBusy] = useState(false);
  const [giveawayValue, setGiveawayValue] = useState(60);
  const [giveawayNote, setGiveawayNote] = useState("");
  const generateGiveaway = async () => {
    setGiveawayBusy(true);
    try {
      const { data } = await api.post("/admin/coupons/giveaway", {
        prize_value: parseFloat(giveawayValue) || 60,
        valid_days: 90,
        winner_note: giveawayNote || null,
      });
      try { await navigator.clipboard.writeText(data.code); } catch {}
      toast.success(`Copied ${data.code} — $${data.discount_value} off, 1 use, 90-day expiry`);
      setGiveawayNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate giveaway code");
    } finally {
      setGiveawayBusy(false);
    }
  };

  return (
    <div data-testid="admin-coupons">
      {/* Giveaway generator strip */}
      <div className="border-2 border-red-900/60 bg-gradient-to-r from-red-950/40 via-zinc-950 to-zinc-950 p-5 mb-6" data-testid="giveaway-generator">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4 text-red-500" />
          <div className="font-mono-tech text-xs text-red-400 uppercase tracking-widest">Giveaway Generator</div>
        </div>
        <p className="font-mono-tech text-[11px] text-zinc-400 leading-relaxed mb-4">
          // Creates a unique single-use code (e.g. <span className="text-zinc-200">GIVE-A7X9KB</span>) that gives the winner $X off any item.
          The code is 1-use only and bound to the winner's account after they redeem it — so it can't be shared or re-used.
          Auto-copies to your clipboard. Send it via Discord/email to your winner.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-3 items-end">
          <div>
            <label className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest block mb-1">$ Off</label>
            <input
              type="number"
              min="1"
              step="1"
              value={giveawayValue}
              onChange={(e) => setGiveawayValue(e.target.value)}
              className={fieldCls}
              data-testid="giveaway-value-input"
            />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase text-zinc-500 tracking-widest block mb-1">Winner note (optional)</label>
            <input
              type="text"
              maxLength={120}
              placeholder="e.g. @discord_user - Halloween IG giveaway"
              value={giveawayNote}
              onChange={(e) => setGiveawayNote(e.target.value)}
              className={fieldCls}
              data-testid="giveaway-note-input"
            />
          </div>
          <button
            type="button"
            onClick={generateGiveaway}
            disabled={giveawayBusy}
            data-testid="giveaway-generate-btn"
            className="btn-brand disabled:opacity-50 px-5 py-2.5 font-mono-tech text-xs uppercase tracking-widest whitespace-nowrap"
          >
            {giveawayBusy ? "Generating…" : "Generate Code"}
          </button>
        </div>
        <div className="font-mono-tech text-[10px] text-zinc-600 uppercase tracking-widest mt-2">
          Default: $60 off (covers any tee + shipping) · 1 redemption · 90-day expiry · account required
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create form */}
        <form onSubmit={create} className="lg:col-span-1 border-2 border-zinc-800 bg-zinc-950 p-5 h-fit" data-testid="coupon-create-form">
          <div className="font-mono-tech text-xs text-zinc-200 uppercase mb-3 flex items-center gap-2">
            <Plus className="w-3 h-3" /> New coupon
          </div>
          <div className="space-y-3">
            <Field label="Code *">
              <input required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} className={fieldCls} placeholder="WELCOME10" data-testid="coupon-code-input" />
            </Field>
            <Field label="Type *">
              <select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))} className={fieldCls} data-testid="coupon-type-select">
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed ($)</option>
              </select>
            </Field>
            <Field label={form.discount_type === "percent" ? "Percent Off *" : "Dollars Off *"}>
              <input required type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))} className={fieldCls} data-testid="coupon-value-input" />
            </Field>
            <Field label="Expires (optional)">
              <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className={fieldCls} data-testid="coupon-expires-input" />
            </Field>
            <Field label="Max Uses (optional)">
              <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))} className={fieldCls} placeholder="Unlimited" data-testid="coupon-max-uses-input" />
            </Field>
            <Field label="Min Order $ (optional)">
              <input type="number" min="0" step="0.01" value={form.min_order} onChange={(e) => setForm((f) => ({ ...f, min_order: e.target.value }))} className={fieldCls} placeholder="No minimum" data-testid="coupon-min-order-input" />
            </Field>
            <label className="flex items-center gap-2 pt-1 cursor-pointer" data-testid="coupon-once-per-customer-label">
              <input
                type="checkbox"
                checked={form.once_per_customer}
                onChange={(e) => setForm((f) => ({ ...f, once_per_customer: e.target.checked }))}
                className="accent-red-600 w-4 h-4"
                data-testid="coupon-once-per-customer-input"
              />
              <span className="font-mono-tech text-xs text-zinc-300 uppercase tracking-widest">One per customer</span>
            </label>
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="coupon-create-btn"
            className="mt-4 w-full btn-brand disabled:opacity-50 px-4 py-2.5 font-mono-tech text-xs uppercase tracking-widest"
          >
            {creating ? "Creating…" : "Create Coupon"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-2 border-2 border-zinc-800 bg-zinc-950 p-5" data-testid="coupon-list">
          <div className="font-mono-tech text-xs text-zinc-200 uppercase mb-3 flex items-center gap-2">
            <Tag className="w-3 h-3" /> Active codes {coupons.length > 0 && <span className="text-zinc-500">({coupons.length})</span>}
          </div>
          {loading ? (
            <div className="font-mono-tech text-xs text-zinc-500 uppercase">// Loading…</div>
          ) : coupons.length === 0 ? (
            <div className="font-mono-tech text-xs text-zinc-500 uppercase">// No coupons yet. Create your first code on the left.</div>
          ) : (
            <div className="space-y-2">
              {coupons.map((c) => {
                const expired = c.expires_at && new Date(c.expires_at) < new Date();
                const maxedOut = c.max_uses != null && c.times_used >= c.max_uses;
                return (
                  <div
                    key={c.id}
                    data-testid={`coupon-row-${c.code}`}
                    className={`border-2 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 ${
                      c.active && !expired && !maxedOut
                        ? "border-red-900/40 bg-zinc-900/40"
                        : "border-zinc-800 bg-zinc-950 opacity-60"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-2xl text-zinc-50 tracking-wider">{c.code}</span>
                        {!c.active && <span className="font-mono-tech text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 uppercase">Paused</span>}
                        {expired && <span className="font-mono-tech text-[10px] px-2 py-0.5 bg-red-900/40 text-red-400 uppercase">Expired</span>}
                        {maxedOut && <span className="font-mono-tech text-[10px] px-2 py-0.5 bg-red-900/40 text-red-400 uppercase">Maxed out</span>}
                      </div>
                      <div className="mt-1 font-mono-tech text-xs text-zinc-400 uppercase tracking-widest">
                        {c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value.toFixed(2)} off`}
                        {c.min_order != null && <> · min ${c.min_order.toFixed(2)}</>}
                        {c.max_uses != null && <> · {c.times_used}/{c.max_uses} used</>}
                        {c.max_uses == null && c.times_used > 0 && <> · {c.times_used} used</>}
                        {c.once_per_customer && <> · <span className="text-red-400">1/customer</span></>}
                        {c.expires_at && <> · exp {new Date(c.expires_at).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleActive(c)}
                        data-testid={`coupon-toggle-${c.code}`}
                        className="font-mono-tech text-[10px] px-3 py-1.5 border-2 border-zinc-700 text-zinc-300 hover:border-zinc-500 uppercase"
                      >
                        {c.active ? "Pause" : "Activate"}
                      </button>
                      <button
                        type="button"
                        onClick={() => del(c.code)}
                        data-testid={`coupon-delete-${c.code}`}
                        className="p-1.5 border-2 border-zinc-800 text-zinc-400 hover:border-red-600 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
